export type IconResolver = (iconName: string) => Promise<string>

// in dev we should just load from node_modules
const loadIcon: IconResolver = (name: string) =>
  import(`@nordhealth/icons/lib/assets/${name}.js`).then(({ default: svg }) => svg)

// in prod we should load from the CDN, as a sensible default
const loadIconCdn: IconResolver = (name: string) =>
  fetch(`https://nordcdn.net/ds/icons/${process.env.ICON_VERSION}/assets/${name}.svg`).then(response => {
    if (!response.ok) {
      throw new TypeError(`NORD: unknown icon: '${name}'`)
    }

    return response.text()
  })

export class IconManager {
  private cache = new Map<string, string | Promise<string>>()
  resolver: IconResolver = process.env.NODE_ENV === "development" ? loadIcon : loadIconCdn

  resolve(name: string, onResolved: (svg: string) => void) {
    let cached = this.cache.get(name)

    // if it's a string, we can resolve immediately
    if (typeof cached === "string") {
      onResolved(cached)
      return
    }

    // if it's null, then we should initiate a fetch
    if (!cached) {
      cached = this.resolver(name)
        .catch(() => "")
        .then(svg => {
          // replace the promise with the resolved value, for faster resolution next time
          this.cache.set(name, svg)
          return svg
        })

      // store the promise so that duplicate icons do not make separate requests
      this.cache.set(name, cached)
    }

    cached.then(onResolved)
  }

  registerIcon(iconOrName: string | { title: string; default: string }, icon?: string) {
    let name: string | undefined
    let svg: string | undefined

    if (typeof iconOrName === "string") {
      name = iconOrName
      svg = icon
    } else {
      name = iconOrName.title
      svg = iconOrName.default
    }

    // handle errors
    if (!name) {
      throw new Error("name is required when registering an icon")
    }
    if (!svg) {
      throw new Error("icon must not be empty")
    }

    this.cache.set(name, svg)
  }

  clear() {
    this.cache.clear()
  }
}
